import { Plus } from 'lucide-react';
import { Link } from 'react-router';
import { useRequisitions } from '@/api/hooks';
import { LIBELLE_STATUT, type StatutDocument } from '@/api/types';
import { peutEmettreDocuments } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, EtapeBadge, PageHeader, StatutBadge, Vide } from '@/components/common';
import { BandeauxACorriger } from '@/components/documents';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFiltresListe } from '@/lib/filtres-liste';
import { dateFr, montant } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT) as StatutDocument[];

/** Réquisitions (v1.1, § 10) : demandes d'achat des services, à traiter pour les validateurs, à transformer pour les achats. */
export function RequisitionListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const peutEmettre = peutEmettreDocuments(u.habilitations);
  const acheteur = u.profils.some((p) => p === 'CHARGE_ACHATS' || p === 'RESPONSABLE_ACHATS' || p === 'CELLULE_MARCHES');
  const valideur = u.etapes.length > 0;
  const { onglet, statut, setOnglet, setStatut } = useFiltresListe(valideur ? (['a-traiter', 'tous'] as const) : (['tous'] as const), valideur ? 'a-traiter' : 'tous', 'tous');
  const { data, isLoading, error } = useRequisitions(onglet === 'a-traiter' ? { aTraiter: true } : statut === 'TOUS' ? {} : { statut });
  const miennes = (data ?? []).filter((r) => r.emetteur?.id === u.agentId).map((r) => ({ id: r.id, numero: r.numero, titre: r.objet, statut: r.statut, etapeCourante: r.etapeCourante, montant: r.montantEstime, correction: r.correction }));

  return (
    <div>
      <PageHeader
        titre="Réquisitions"
        sousTitre="Demandes d'achat des services : validées par le Directeur, le contrôle de gestion et le Responsable des achats, puis transformées en bon de commande."
        actions={
          peutEmettre && (
            <Button asChild>
              <Link to="/requisitions/nouvelle">
                <Plus className="size-4" /> Nouvelle réquisition
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as 'a-traiter' | 'tous')}>
          <TabsList>
            {valideur && <TabsTrigger value="a-traiter">À traiter</TabsTrigger>}
            <TabsTrigger value="tous">{acheteur || u.habilitations.some((h) => h.structureId === null) ? 'Toutes les réquisitions' : 'Mes réquisitions et celles de ma structure'}</TabsTrigger>
          </TabsList>
        </Tabs>
        {onglet === 'tous' && (
          <Select value={statut} onValueChange={(v) => setStatut(v as StatutDocument | 'TOUS')}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TOUS">Tous les statuts</SelectItem>
              {STATUTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {LIBELLE_STATUT[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {onglet === 'tous' && <div className="mb-4"><BandeauxACorriger documents={miennes} chemin={(id) => `/requisitions/${id}`} libelle="Réquisition" /></div>}
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucune réquisition en attente de votre décision.' : 'Aucune réquisition.'} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead className="hidden md:table-cell">Structure</TableHead>
                <TableHead className="hidden lg:table-cell">Émetteur</TableHead>
                <TableHead className="text-right">Estimation (USD)</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden sm:table-cell">Mis à jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Link to={`/requisitions/${r.id}`} className="font-medium text-primary hover:underline">
                      {r.numero ?? 'Brouillon'}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[32ch] truncate">
                    {r.objet}
                    {r.urgence && <Badge variant="destructive" className="ml-2">urgente</Badge>}
                    {r.memorial && <Badge variant="outline" className="ml-2">Mémorial</Badge>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{r.structureCode}</TableCell>
                  <TableCell className="hidden lg:table-cell">{r.emetteur?.nom}</TableCell>
                  <TableCell className="num text-right">{montant(r.montantEstime, true)}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutBadge statut={r.statut} />
                    {r.statut === 'SOUMIS' && <EtapeBadge etape={r.etapeCourante} />}
                    {r.statut === 'A_CORRIGER' && r.correction && <span className="text-xs text-amber-800">avant le {dateFr(r.correction.dateLimite)}</span>}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(r.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
